import { animate, style, transition, trigger } from '@angular/animations';
import { CurrencyPipe, JsonPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { StarRatingComponent } from '@comparit/core';
import type { TemplateRef } from '@angular/core';
import type { MarkedItem } from '@comparit/shared';
import { ENABLE_TEST_DATA_VIEWER } from '../../misc/enable-test-data-viewer.token';
import type {
  ErgebnisInput,
  KombinationRelationInput,
} from '../../interfaces/ergebnis-input.model';
import { ResultsBaseService } from '../../services/results-base.service';
import { RatingTooltipComponent } from '../rating-tooltip/rating-tooltip.component';
import { ResultsItemComponent } from '../results-item/results-item.component';

/** A priced result: premium, rating, "Weiter", the compare checkbox, the Bausteine chips. */
@Component({
  selector: 'ui-results-item-success',
  templateUrl: './results-item-success.component.html',
  styleUrl: './results-item-success.component.scss',
  imports: [
    CurrencyPipe,
    JsonPipe,
    NgClass,
    NgTemplateOutlet,
    MatIcon,
    MatCheckbox,
    MatButtonModule,
    ResultsItemComponent,
    StarRatingComponent,
    RatingTooltipComponent,
  ],
  animations: [
    trigger('slideDownUp', [
      transition(':enter', [
        style({ height: 0 }),
        animate('250ms ease-out', style({ height: '*' })),
      ]),
      transition(':leave', [
        style({ height: '*' }),
        animate('250ms ease-in', style({ height: 0, opacity: 0 })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultsItemSuccessComponent {
  private readonly results: ResultsBaseService<unknown, ErgebnisInput> = inject(ResultsBaseService);
  protected readonly enableTestDataViewer = inject(ENABLE_TEST_DATA_VIEWER);

  readonly ergebnis = input.required<ErgebnisInput>();
  readonly icon = input<string>();
  readonly isBest = input(false);
  /** Set when the brand lists every kombination flat; the card then shows no chips and no children. */
  readonly ungrouped = input(false);
  /** The Sparte's detail panel; gets this card's `ergebnis` as the implicit context value. */
  readonly itemDetails = input<TemplateRef<{ $implicit: ErgebnisInput }>>();

  readonly selected = output<MarkedItem>();

  protected readonly beitragCaption = this.results.beitragCaption;
  protected readonly detailsOpen = signal(false);
  protected readonly jsonExpand = signal(false);

  private readonly compare = toSignal(this.results.calculatorForm.compare$, {
    initialValue: this.results.calculatorForm.ctrls.compare.value,
  });
  protected readonly checked = computed(() =>
    this.compare().some(item => item.kombinationId === this.ergebnis().kombinationId)
  );
  /** Three tariffs compare at most; the marked ones can still be unmarked. */
  protected readonly compareDisabled = computed(() => !this.checked() && this.compare().length > 2);

  protected readonly asMarkedItem = computed<MarkedItem>(() => {
    const { berechnungId, kombinationId } = this.ergebnis();
    return { berechnungId, kombinationId };
  });

  /**
   * Read off the control rather than `bausteine$`: that stream's `startWith` captures the value
   * when CalculatorForm is built, so a card created after a recalculation — which re-marks the
   * Bausteine while results arrive — would otherwise start from a stale empty list.
   */
  private readonly bausteineCtrl = this.results.calculatorForm.ctrls.bausteine;
  private readonly bausteine = toSignal(this.bausteineCtrl.valueChanges, {
    initialValue: this.bausteineCtrl.value,
  });

  /**
   * The siblings the customer expanded under this card. Every kombination of a container arrives in
   * the same hub batch, so a chip resolves against the results already in hand — no recalculation.
   */
  protected readonly children = computed(() => {
    const kombinationId = this.ergebnis().kombinationId;
    return this.bausteine()
      .filter(item => item.parentKombinationId === kombinationId)
      .map(item =>
        this.results
          .all()
          .find(r => r.kombinationId === item.kombinationId && r.berechnungId === item.berechnungId)
      )
      .filter((item): item is ErgebnisInput => !!item?.successful);
  });

  protected toggleCompare(): void {
    this.results.calculatorForm.toggleCompare(this.asMarkedItem());
  }

  protected toggleBausteine(relation: KombinationRelationInput): void {
    const { berechnungId, kombinationId } = this.ergebnis();
    this.results.calculatorForm.toggleBausteine({
      // A cheaper sibling is the Baustein being removed again, so the card re-marks itself: the
      // toggle matches on kombinationId and drops this card from its parent's child list.
      kombinationId: relation.preisDifferenz < 0 ? kombinationId : relation.kombinationsId,
      berechnungId,
      parentKombinationId: kombinationId,
    });
  }
}
